import React from 'react';

/**
 * Renders the shared semantic section shell for a guided narrative chapter.
 *
 * @param {object} props - Chapter content and presentation configuration.
 * @param {number} props.index - Zero-based guided-scroll chapter index.
 * @param {string} props.id - Stable section ID used by the title relationship.
 * @param {string} [props.tone='light'] - Visual tone class suffix.
 * @param {React.ReactNode} [props.eyebrow] - Optional introductory label.
 * @param {React.ReactNode} props.title - Chapter heading content.
 * @param {React.ReactNode} [props.body] - Optional chapter description.
 * @param {React.ReactNode} [props.children] - Optional interactive or supporting content.
 * @returns {React.ReactElement} An aria-labelled narrative section.
 * @sideEffects Has no external side effects beyond React rendering.
 */
export default function NarrativeChapter({
  index,
  id,
  tone = 'light',
  eyebrow,
  title,
  body,
  children,
}) {
  if (typeof id !== 'string' || id.trim().length === 0) {
    throw new TypeError('NarrativeChapter requires a nonempty id.');
  }

  if (!Number.isSafeInteger(index) || index < 0) {
    throw new TypeError('NarrativeChapter index must be a non-negative integer.');
  }

  if (title === null || title === undefined) {
    throw new TypeError('NarrativeChapter requires a title.');
  }

  return (
    <section
      className={'narrative-chapter narrative-chapter--' + tone}
      data-chapter-index={index}
      id={id}
      aria-labelledby={id + '-title'}
    >
      <div className="narrative-chapter__content">
        {eyebrow ? <p className="chapter-eyebrow">{eyebrow}</p> : null}
        <h2 id={id + '-title'} className="chapter-title">{title}</h2>
        {body !== null && body !== undefined
          ? <div className="chapter-body">{body}</div>
          : null}
        {children}
      </div>
    </section>
  );
}
